import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";

import {
  CLI_ERROR_PATH,
  clearCliOauthRequestId,
  cliConsentUri,
  readCliOauthRequestId,
} from "utils/cli-oauth";
import { Logger } from "utils/logger";

/*
 * Auth0 returns the CLI login here (the redirect_uri the providers used). The SDK
 * finishes the code exchange; posting the Auth0 token to `/auth/auth0` is what
 * establishes the platform session the consent screen needs. Then the browser
 * goes back to consent, which is where the code is actually minted.
 */
const CliOauthCallback: React.FC = (): JSX.Element => {
  const { error, getAccessTokenSilently, isAuthenticated, isLoading } =
    useAuth0();

  useEffect((): void => {
    if (isLoading) {
      return;
    }

    async function complete(): Promise<void> {
      const requestId = readCliOauthRequestId();
      if (error || !isAuthenticated || requestId === null) {
        Logger.error("CLI OAuth callback not authenticated", {
          error: error?.message,
        });
        clearCliOauthRequestId();
        window.location.assign(CLI_ERROR_PATH);

        return;
      }
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${window.location.origin}/auth/auth0`, {
          body: JSON.stringify({ token }),
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          method: "POST",
        });
        if (!response.ok) {
          clearCliOauthRequestId();
          window.location.assign(CLI_ERROR_PATH);

          return;
        }
        window.location.assign(cliConsentUri(requestId));
      } catch (caught) {
        Logger.error("CLI OAuth callback failed", { error: caught });
        clearCliOauthRequestId();
        window.location.assign(CLI_ERROR_PATH);
      }
    }

    void complete();
  }, [error, getAccessTokenSilently, isAuthenticated, isLoading]);

  return <p>{"Completing sign in..."}</p>;
};

export { CliOauthCallback };
